import { ReactNode } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from './AuthContext';
import { LoadingSpinner } from '../components/LoadingSpinner';

interface AdminGuardProps {
  children: ReactNode;
  requireAdmin?: boolean; // If true, requires ADMIN role
  requireModerator?: boolean; // If true, requires MODERATOR or ADMIN role
}

export function AdminGuard({ children, requireAdmin = false, requireModerator = false }: AdminGuardProps) {
  const { isLoading, isAuthenticated, isAdmin, isModerator, role } = useAuth();
  const location = useLocation();

  // Show loading while checking auth
  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <LoadingSpinner size="lg" />
          <p className="mt-4 text-gray-600">Verifying access...</p>
        </div>
      </div>
    );
  }

  // Redirect to login if not authenticated
  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // Check role-based access
  if (requireAdmin && !isAdmin) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center max-w-md">
          <div className="text-6xl mb-4">🔒</div>
          <h1 className="text-2xl font-bold text-gray-900 mb-2">
            Admin Access Required
          </h1>
          <p className="text-gray-600 mb-4">
            Your current role ({role}) does not have access to this section.
            Please contact an administrator if you believe this is an error.
          </p>
          <a href="/" className="btn btn-primary">
            Return to Dashboard
          </a>
        </div>
      </div>
    );
  }

  if (requireModerator && !isModerator) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center max-w-md">
          <div className="text-6xl mb-4">🔒</div>
          <h1 className="text-2xl font-bold text-gray-900 mb-2">
            Moderator Access Required
          </h1>
          <p className="text-gray-600 mb-4">
            Your current role ({role}) does not have access to this section.
            Please contact an administrator if you believe this is an error.
          </p>
          <a href="/" className="btn btn-primary">
            Return Home
          </a>
        </div>
      </div>
    );
  }

  // User is authorized
  return <>{children}</>;
}

/**
 * Higher-order component to wrap routes that require admin-only access
 */
export function AdminOnlyGuard({ children }: { children: ReactNode }) {
  return <AdminGuard requireAdmin>{children}</AdminGuard>;
}

/**
 * Higher-order component to wrap routes that require moderator or admin access
 */
export function ModeratorGuard({ children }: { children: ReactNode }) {
  return <AdminGuard requireModerator>{children}</AdminGuard>;
}
